import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGuardianPaths } from '../../../../hooks/useGuardianPaths';
import {
  StudentProfile,
  StudentProgress,
  StudentAttendance,
  CoachNote,
  Student,
  hasAnyRole,
  TierFeatureKey,
} from '@court-sync/shared';
import { useAcademyFeatureAccess } from '@/hooks/useFeatureAccess';
import { useAuthStore } from '../../../../store/auth';
import { useGuardianAcademyStore } from '../../../../store/guardianAcademy';
import { StudentCertificatesPanel } from '@/components/students/StudentCertificatesPanel';
import { Badge, Button, Card, CardHeader, CardContent } from '@/components/ui';
import {
  AttendanceSummary,
  ProgressChart,
  CoachNotesPanel,
} from '../../../../components/students';
import { studentService } from '../../../../services/student';
import { studentProgressService } from '../../../../services/studentProgressService';
import { coachNotesService, COACH_NOTES_PAGE_SIZE } from '../../../../services/coachNotesService';
import type { PaginationState } from '../../../../components/ui/PaginatedTable';
import {
  User,
  Users,
  Calendar,
  TrendingUp,
  MessageSquare,
  ChevronRight,
  Phone,
  Heart,
  Award,
} from 'lucide-react';
import { cn } from '../../../../utils/cn';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import { DashboardStatCard } from '@/components/dashboard/DashboardStatCard';

type ViewMode = 'list' | 'detail';
type TabType = 'overview' | 'attendance' | 'progress' | 'notes' | 'certificates';

const myStudentsHelpShort = <p>View your students</p>;
const myStudentsHelpFull = <p>View your profile and any students you have added</p>;

const TABS: { id: TabType; label: string; mobileLabel?: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'Overview', icon: <User className="w-4 h-4" /> },
  { id: 'attendance', label: 'Attendance', icon: <Calendar className="w-4 h-4" /> },
  { id: 'progress', label: 'Progress', icon: <TrendingUp className="w-4 h-4" /> },
  { id: 'notes', label: 'Coach Notes', mobileLabel: 'Notes', icon: <MessageSquare className="w-4 h-4" /> },
  { id: 'certificates', label: 'Certificates', mobileLabel: 'Certs', icon: <Award className="w-4 h-4" /> },
];

const calculateAge = (dateOfBirth: Date | undefined | null): number | null => {
  if (!dateOfBirth) return null;
  const birthDate = new Date(dateOfBirth);
  if (Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const getSkillLevelVariant = (level: string): 'default' | 'secondary' | 'success' | 'warning' => {
  switch (level) {
    case 'advanced':
      return 'success';
    case 'intermediate':
      return 'warning';
    default:
      return 'secondary';
  }
};

export default function MyStudents() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const paths = useGuardianPaths();
  const academyId = useGuardianAcademyStore((s) => s.academyId);
  const academy = useGuardianAcademyStore((s) => s.academy);
  const { hasAccess: hasProgramsAccess } = useAcademyFeatureAccess(academy, TierFeatureKey.PROGRAMS);

  const detailTabs = useMemo(
    () => (hasProgramsAccess ? TABS : TABS.filter((t) => t.id !== 'certificates')),
    [hasProgramsAccess]
  );

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedStudent, setSelectedStudent] = useState<StudentProfile | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [students, setStudents] = useState<StudentProfile[]>([]);
  const [progress, setProgress] = useState<StudentProgress | null>(null);
  const [attendance, setAttendance] = useState<StudentAttendance[]>([]);
  const [notes, setNotes] = useState<CoachNote[]>([]);
  const [notesPage, setNotesPage] = useState(1);
  const [notesPagination, setNotesPagination] = useState<PaginationState | null>(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectedAge = useMemo(
    () => (selectedStudent ? calculateAge(selectedStudent.dateOfBirth) : null),
    [selectedStudent]
  );

  // Check access
  useEffect(() => {
    if (!user || !hasAnyRole(user, ['guardian', 'student'])) {
      navigate('/dashboard');
      return;
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!hasProgramsAccess && activeTab === 'certificates') {
      setActiveTab('overview');
    }
  }, [hasProgramsAccess, activeTab]);

  // Load students list (reload when academy context changes)
  useEffect(() => {
    if (user) {
      loadStudents();
    }
  }, [user, academyId]);

  useLayoutEffect(() => {
    setNotesPage(1);
  }, [selectedStudent?.id]);

  const fetchNotesPage = useCallback(
    async (page: number) => {
      if (!academyId || !selectedStudent?.id) return;
      setNotesLoading(true);
      try {
        const res = await coachNotesService.getStudentNotes(selectedStudent.id, academyId, false, {
          page,
          limit: COACH_NOTES_PAGE_SIZE,
        });
        setNotes(res.data || []);
        setNotesPagination(res.pagination ?? null);
      } catch (err) {
        console.error('Failed to load coach notes:', err);
      } finally {
        setNotesLoading(false);
      }
    },
    [academyId, selectedStudent?.id]
  );

  useEffect(() => {
    if (!selectedStudent?.id || !academyId) return;
    void fetchNotesPage(notesPage);
  }, [selectedStudent?.id, academyId, notesPage, fetchNotesPage]);

  // Load selected student details
  useEffect(() => {
    if (selectedStudent) {
      loadStudentDetails(selectedStudent.id);
    }
  }, [selectedStudent]);

  const loadStudents = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Get students scoped to the current academy
      const response = await studentService.getStudents(academyId || undefined);
      let studentList: Student[] = response.data || [];

      // Deduplicate: ensure no duplicate students (same id or same person with duplicate records)
      const seenIds = new Set<string>();
      const seenSelfKey = new Set<string>(); // "firstName|lastName" for isSelf to catch duplicate self-student records
      studentList = studentList.filter((s) => {
        if (seenIds.has(s.id)) return false;
        if (s.isSelf) {
          const key = `${(s.firstName ?? '').toLowerCase()}|${(s.lastName ?? '').toLowerCase()}`;
          if (seenSelfKey.has(key)) return false;
          seenSelfKey.add(key);
        }
        seenIds.add(s.id);
        return true;
      });

      // Build StudentProfile objects with basic info
      // For list view, we'll show basic info and load detailed summaries on demand
      const studentProfiles: StudentProfile[] = await Promise.all(
        studentList.map(async (student) => {
          // Get attendance summary scoped to the current academy
          let attendanceSummary: StudentProfile['attendanceSummary'];
          try {
            const attendanceResponse = await studentService.getStudentAttendanceSummary(student.id, academyId || undefined);
            if (attendanceResponse.data) {
              attendanceSummary = {
                totalLessons: attendanceResponse.data.totalLessons,
                attendedLessons: attendanceResponse.data.attendedLessons,
                attendanceRate: attendanceResponse.data.attendanceRate,
              };
            }
          } catch (err) {
            // If attendance fails, continue without it
            console.warn(`Failed to load attendance for student ${student.id}:`, err);
          }

          return {
            ...student,
            skillLevel: student.skillLevel || 'beginner',
            attendanceSummary,
            // Progress and notes will be loaded in detail view
          } as StudentProfile;
        })
      );

      setStudents(studentProfiles);

      // Auto-select first student if only one
      if (studentProfiles.length === 1) {
        setSelectedStudent(studentProfiles[0]);
        setViewMode('detail');
      }
    } catch (err) {
      console.error('Error loading students:', err);
      setError('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const loadStudentDetails = async (studentId: string) => {
    if (!user || !academyId) return;

    try {
      // Load progress, attendance, and notes scoped to the current academy
      const [progressResponse, attendanceResponse] = await Promise.all([
        studentProgressService.getStudentProgress(studentId, academyId),
        studentService.getStudentAttendance(studentId, academyId),
      ]);

      setProgress(progressResponse.data || null);
      setAttendance(attendanceResponse.data || []);
    } catch (err) {
      console.error('Error loading student details:', err);
      // Set empty states on error
      setProgress(null);
      setAttendance([]);
      setNotes([]);
    }
  };

  const handleSelectStudent = (student: StudentProfile) => {
    setSelectedStudent(student);
    setViewMode('detail');
    setActiveTab('overview');
  };

  if (!user) {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <div className="contents">
        <PageHeader title="My Students" help={myStudentsHelpShort} />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="contents">
        <PageHeader title="My Students" help={myStudentsHelpShort} />
        <div className="flex flex-col items-center justify-center py-20">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <Button onClick={loadStudents}>Retry</Button>
        </div>
      </div>
    );
  }

  // Empty state
  if (students.length === 0) {
    return (
      <div className="contents">
        <PageHeader title="My Students" help={myStudentsHelpFull} />
        <Card>
          <CardContent className="p-12 text-center">
            <User className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Students Yet
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Add a student to start tracking their progress and attendance.
            </p>
            <Button onClick={() => navigate(paths?.profile ?? '/academy-picker')}>
              Add Student
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="contents">
        <PageHeader title="My Students" help={myStudentsHelpFull} />
        {viewMode === 'list' && (
          <div className="space-y-4">
            <div className="mb-4 grid grid-cols-2 gap-2 sm:max-w-xs sm:gap-4">
              <DashboardStatCard
                icon={<Users className="text-primary-600 dark:text-primary-400" aria-hidden />}
                iconBg="bg-primary-100 dark:bg-primary-900/30"
                iconColor="text-primary-600 dark:text-primary-400"
                label="My Students"
                mobileLabel="Students"
                value={students.length}
              />
            </div>
            <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
              View attendance, progress, and coach notes for your children.
            </p>
            {students.map((student) => {
              const age = calculateAge(student.dateOfBirth);
              const isSelfStudent = student.isSelf === true;
              return (
                <Card
                  key={student.id}
                  className="cursor-pointer transition-all hover:shadow-md hover:border-primary-300"
                  onClick={() => handleSelectStudent(student)}
                >
                  <CardContent className="p-3 sm:p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30 sm:h-14 sm:w-14">
                          <User className="h-5 w-5 text-primary-600 dark:text-primary-400 sm:h-7 sm:w-7" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <h3 className="truncate text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
                              {student.firstName} {student.lastName}
                            </h3>
                            {isSelfStudent && (
                              <Badge variant="secondary" className="text-xs">
                                Me
                              </Badge>
                            )}
                          </div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                            {age != null && <span>Age {age}</span>}
                            <Badge variant={getSkillLevelVariant(student.skillLevel)}>
                              {student.skillLevel}
                            </Badge>
                            {student.attendanceSummary && (
                              <span
                                className={cn(
                                  'font-medium tabular-nums sm:hidden',
                                  student.attendanceSummary.attendanceRate >= 90
                                    ? 'text-green-600'
                                    : student.attendanceSummary.attendanceRate >= 70
                                      ? 'text-yellow-600'
                                      : 'text-red-600'
                                )}
                              >
                                {student.attendanceSummary.attendanceRate}% attendance
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex shrink-0 items-center gap-3 sm:gap-6">
                        {student.attendanceSummary && (
                          <div className="hidden text-right sm:block">
                            <div className="text-sm text-gray-500 dark:text-gray-400">Attendance</div>
                            <div
                              className={cn(
                                'text-lg font-semibold tabular-nums',
                                student.attendanceSummary.attendanceRate >= 90
                                  ? 'text-green-600'
                                  : student.attendanceSummary.attendanceRate >= 70
                                    ? 'text-yellow-600'
                                    : 'text-red-600'
                              )}
                            >
                              {student.attendanceSummary.attendanceRate}%
                            </div>
                          </div>
                        )}
                        <ChevronRight className="h-5 w-5 text-gray-400" aria-hidden />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Detail View */}
        {viewMode === 'detail' && selectedStudent && (
          <>
            {/* Student Header Card */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                    <User className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        {selectedStudent.firstName} {selectedStudent.lastName}
                      </h2>
                      {selectedStudent.isSelf === true && (
                        <Badge variant="secondary" className="text-xs">
                          Me
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      {selectedAge != null && (
                        <span className="text-gray-500 dark:text-gray-400">
                          Age {selectedAge}
                        </span>
                      )}
                      <Badge variant={getSkillLevelVariant(selectedStudent.skillLevel)}>
                        {selectedStudent.skillLevel}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <DashboardTabNav<TabType>
              tabs={detailTabs}
              activeTab={activeTab}
              onChange={setActiveTab}
              accent="primary"
              ariaLabel="Student sections"
            />

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <div className="grid gap-6 md:grid-cols-2">
                {/* Quick Stats */}
                <Card>
                  <CardHeader>
                    <h3 className="font-semibold text-gray-900 dark:text-white">Overview</h3>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2 sm:gap-4">
                      {selectedStudent.attendanceSummary && (
                        <DashboardStatCard
                          icon={<Calendar className="text-primary-600 dark:text-primary-400" aria-hidden />}
                          iconBg="bg-primary-100 dark:bg-primary-900/30"
                          iconColor="text-primary-600 dark:text-primary-400"
                          label="Attendance"
                          value={`${selectedStudent.attendanceSummary.attendanceRate}%`}
                        />
                      )}
                      {selectedStudent.progressSummary && (
                        <DashboardStatCard
                          icon={<TrendingUp className="text-green-600 dark:text-green-400" aria-hidden />}
                          iconBg="bg-green-100 dark:bg-green-900"
                          iconColor="text-green-600 dark:text-green-400"
                          label="Achievements"
                          value={selectedStudent.progressSummary.recentAchievements.length}
                        />
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Emergency Contact */}
                <Card>
                  <CardHeader>
                    <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Emergency Contact
                    </h3>
                  </CardHeader>
                  <CardContent>
                    {selectedStudent.emergencyContact ? (
                      <div className="space-y-2 text-sm">
                        <p><span className="text-gray-500">Name:</span> {selectedStudent.emergencyContact.name}</p>
                        <p><span className="text-gray-500">Phone:</span> {selectedStudent.emergencyContact.phone}</p>
                        <p><span className="text-gray-500">Relationship:</span> {selectedStudent.emergencyContact.relationship}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No emergency contact on file</p>
                    )}
                  </CardContent>
                </Card>

                {/* Medical Notes */}
                {selectedStudent.medicalNotes && (
                  <Card className="md:col-span-2">
                    <CardHeader>
                      <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <Heart className="w-4 h-4" />
                        Medical Notes
                      </h3>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {selectedStudent.medicalNotes}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {activeTab === 'attendance' && (
              <>
                {attendance.length > 0 ? (
                  <AttendanceSummary attendance={attendance} showDetails />
                ) : (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No Attendance Records Yet
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400">
                        Attendance will be tracked once enrolled in lessons.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {activeTab === 'progress' && (
              <>
                {progress ? (
                  <ProgressChart progress={progress} showAchievements />
                ) : (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No Progress Data Yet
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400">
                        Progress will be recorded after the first assessment.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {activeTab === 'certificates' && selectedStudent && academyId && (
              <StudentCertificatesPanel
                academyId={academyId}
                studentId={selectedStudent.id}
                studentFirstName={selectedStudent.firstName}
                studentLastName={selectedStudent.lastName}
                academy={academy}
              />
            )}

            {activeTab === 'notes' && (
              <CoachNotesPanel
                notes={notes}
                notesPagination={notesPagination}
                onNotesPageChange={setNotesPage}
                notesLoading={notesLoading}
                isCoach={false}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
